import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";

// ── reCAPTCHA v3 ────────────────────────────────────────────────────────────
const RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MEvWXzeV"; // test key — replace with real one
const loadRecaptcha = () => new Promise((resolve) => {
  if (window.grecaptcha) { resolve(); return; }
  const s = document.createElement("script");
  s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  s.onload = () => window.grecaptcha.ready(resolve);
  document.head.appendChild(s);
});
const getToken = () => new Promise((resolve, reject) => {
  window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "enroll" })
    .then(resolve).catch(reject);
});

// ── styles ───────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: "100vh", background: "#F4F7FB", fontFamily: "'Nunito', 'DM Sans', system-ui, sans-serif", padding: "32px 16px 64px" },
  card: { background: "#fff", borderRadius: 20, border: "1px solid #E5E7EB", padding: "32px 36px", maxWidth: 680, margin: "0 auto", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" },
  header: { textAlign: "center", marginBottom: 32 },
  logoBox: { width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg,#E8679A,#F48FB1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: "#fff", fontSize: 28, fontWeight: 900 },
  schoolName: { margin: "0 0 4px", fontSize: 24, fontWeight: 800, color: "#1A2B4A" },
  subtitle: { margin: 0, fontSize: 14, color: "#6B7B90" },
  section: { marginBottom: 28 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: "#6B7B90", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid #F3F4F6" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, fontWeight: 700, color: "#4B5563", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.3 },
  input: { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #E5E7EB", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#FAFAFA", transition: "border-color 0.15s" },
  inputFocus: { borderColor: "#E8679A", background: "#fff" },
  select: { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #E5E7EB", fontSize: 14, fontFamily: "inherit", background: "#FAFAFA", cursor: "pointer" },
  required: { color: "#E8679A", marginLeft: 2 },
  addBtn: { background: "none", border: "1.5px dashed #D1D5DB", borderRadius: 10, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: "#9CA3AF", cursor: "pointer", width: "100%", marginTop: 8, fontFamily: "inherit" },
  removeBtn: { background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, padding: "4px 8px" },
  submitBtn: { width: "100%", padding: "14px 0", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#E8679A,#F48FB1)", color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginTop: 8, transition: "opacity 0.15s" },
  error: { background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#DC2626", marginBottom: 16, fontWeight: 600 },
  success: { textAlign: "center", padding: "40px 20px" },
  check: { fontSize: 64, marginBottom: 16 },
  progressBar: { display: "flex", gap: 8, marginBottom: 32, justifyContent: "center" },
  stepDot: (active, done) => ({ width: 32, height: 6, borderRadius: 3, background: done ? "#E8679A" : active ? "#F48FB1" : "#E5E7EB", transition: "background 0.3s" }),
};

// ── field components ─────────────────────────────────────────────────────────
const Field = ({ label, required: req, children, half }) => (
  <div style={{ ...S.field, ...(half ? {} : {}) }}>
    <label style={S.label}>{label}{req && <span style={S.required}>*</span>}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text", ...rest }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{ ...S.input, ...(focused ? S.inputFocus : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...rest}
    />
  );
};

const Select = ({ value, onChange, options }) => (
  <select value={value} onChange={onChange} style={S.select}>
    {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
  </select>
);

const AGE_GROUPS = ["Infant", "Toddler", "Two's", "Pre-K", "VPK", "Kindergarten", "First Grade", "Second Grade", "Third Grade", "Fourth Grade", "Fifth Grade"];
const STEPS = ["Child", "Guardian", "Medical", "Emergency"];

// ── main component ───────────────────────────────────────────────────────────
const PublicEnrollmentForm = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const schoolId = urlParams.get("school");

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    // Child
    childFirst: "", childMiddle: "", childLast: "", dob: "", gender: "",
    ageGroup: "", address: "", desiredStart: "", previousSchool: "", referralSource: "",
    // Guardian 1
    parentName: "", parentRelation: "Mother", parentPhone: "", parentEmail: "",
    parentEmployer: "", parentWorkPhone: "",
    // Guardian 2
    parent2Name: "", parent2Relation: "", parent2Phone: "", parent2Email: "",
    parent2Employer: "", parent2WorkPhone: "",
    // Medical
    allergies: "", medications: "", specialNeeds: "",
    physician: "", physicianPhone: "", insuranceProvider: "", insurancePolicy: "",
    // Emergency
    emergencyContacts: [{ name: "", relation: "", phone: "" }],
    authorizedPickups: [{ name: "", relation: "" }],
  });

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  // Load school info
  useEffect(() => {
    if (!schoolId) { setNotFound(true); setLoading(false); return; }
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from("schools").select("id, name, logo").eq("id", schoolId).maybeSingle();
        if (error || !data) { setNotFound(true); }
        else setSchool(data);
      } catch { setNotFound(true); }
      setLoading(false);
    };
    load();
    loadRecaptcha().catch(() => {});
  }, [schoolId]);

  // Auto-suggest age group from DOB
  useEffect(() => {
    if (!form.dob) return;
    const age = (new Date() - new Date(form.dob)) / (365.25 * 24 * 3600 * 1000);
    let group = "";
    if (age < 1) group = "Infant";
    else if (age < 2) group = "Toddler";
    else if (age < 3) group = "Two's";
    else if (age < 5) group = "Pre-K";
    else if (age < 6) group = "VPK";
    else if (age < 7) group = "Kindergarten";
    else group = "First Grade";
    setForm(prev => ({ ...prev, ageGroup: group }));
  }, [form.dob]);

  const validate = () => {
    if (step === 0) {
      if (!form.childFirst.trim()) return "Child's first name is required.";
      if (!form.childLast.trim()) return "Child's last name is required.";
      if (!form.dob) return "Date of birth is required.";
    }
    if (step === 1) {
      if (!form.parentName.trim()) return "Guardian name is required.";
      if (!form.parentPhone.trim()) return "Guardian phone is required.";
      if (!form.parentEmail.trim()) return "Guardian email is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail)) return "Please enter a valid email address.";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => { setError(""); setStep(s => s - 1); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true); setError("");

    try {
      // Get reCAPTCHA token
      let captchaToken = null;
      try { captchaToken = await getToken(); } catch { /* non-blocking */ }

      // Build form_data matching the app's internal format
      const formData = {
        childFirst: form.childFirst.trim(),
        childMiddle: form.childMiddle.trim(),
        childLast: form.childLast.trim(),
        dob: form.dob,
        gender: form.gender,
        ageGroup: form.ageGroup,
        address: form.address.trim(),
        desiredStart: form.desiredStart || "",
        previousSchool: form.previousSchool.trim(),
        referralSource: form.referralSource.trim(),
        allergies: form.allergies.trim(),
        medications: form.medications.trim(),
        specialNeeds: form.specialNeeds.trim(),
        physician: form.physician.trim(),
        physicianPhone: form.physicianPhone.trim(),
        insuranceProvider: form.insuranceProvider.trim(),
        insurancePolicy: form.insurancePolicy.trim(),
        parentName: form.parentName.trim(),
        parentRelation: form.parentRelation,
        parentPhone: form.parentPhone.trim(),
        parentEmail: form.parentEmail.trim().toLowerCase(),
        parentEmployer: form.parentEmployer.trim(),
        parentWorkPhone: form.parentWorkPhone.trim(),
        parent2Name: form.parent2Name.trim(),
        parent2Relation: form.parent2Relation.trim(),
        parent2Phone: form.parent2Phone.trim(),
        parent2Email: form.parent2Email.trim().toLowerCase(),
        parent2Employer: form.parent2Employer.trim(),
        parent2WorkPhone: form.parent2WorkPhone.trim(),
        emergencyContacts: form.emergencyContacts.filter(c => c.name.trim()),
        authorizedPickups: form.authorizedPickups.filter(p => p.name.trim()),
        status: "inquiry",
        submittedVia: "public_form",
        notes: [],
        tourDate: "",
        tourComplete: false,
        immunizationOnFile: false,
        physicalOnFile: false,
        priority: 0,
      };

      const { error: insertError } = await supabase.from("enrollment_apps").insert({
        school_id: schoolId,
        stage: "inquiry",
        child_first: form.childFirst.trim(),
        child_last: form.childLast.trim(),
        form_data: formData,
      });

      if (insertError) throw insertError;

      // Notify the school admin that a new application was submitted
      try {
        await supabase.functions.invoke("send-email", {
          body: {
            type: "new-enrollment",
            payload: {
              schoolId,
              childName: `${form.childFirst.trim()} ${form.childLast.trim()}`,
              parentName: form.parentName.trim(),
              parentEmail: form.parentEmail.trim().toLowerCase(),
            },
          },
        });
      } catch (notifyErr) {
        console.warn("Admin notification failed:", notifyErr);
        // Non-blocking — form submission still succeeds
      }

      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Please try again or contact the school directly.");
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#6B7B90" }}>
        <div style={{ marginBottom: 12, opacity: 0.5 }}>📋</div>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Loading enrollment form…</div>
      </div>
    </div>
  );

  if (notFound) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#6B7B90", maxWidth: 400 }}>
        <div style={{ marginBottom: 12 }}>🔍</div>
        <h2 style={{ color: "#1A2B4A", fontWeight: 800 }}>Form not found</h2>
        <p>This enrollment link is invalid or has expired. Please contact the school for a new link.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ ...S.card, ...S.success }}>
        {school?.logo ? <img src={school.logo} alt="" style={{ height: 64, borderRadius: 14, marginBottom: 16 }} /> : <div style={{ ...S.logoBox, margin: "0 auto 16px" }}>S</div>}
        <div style={{ ...S.check, fontSize: "initial" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#E8F5E9", border: "3px solid #66BB6A", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><path d="M8 21L16 29L32 13" stroke="#66BB6A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
        </div>
        <h2 style={{ margin: "0 0 12px", fontSize: 26, fontWeight: 900, color: "#1A2B4A" }}>Application Submitted!</h2>
        <p style={{ color: "#6B7B90", fontSize: 15, lineHeight: 1.6, maxWidth: 400, margin: "0 auto" }}>
          Thank you for your interest in <strong>{school?.name}</strong>. The school will review your application and be in touch soon.
        </p>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap" rel="stylesheet" />

      <div style={S.card}>
        {/* Header */}
        <div style={S.header}>
          {school?.logo
            ? <img src={school.logo} alt="Logo" style={{ height: 64, borderRadius: 14, marginBottom: 12 }} />
            : <div style={S.logoBox}>S</div>}
          <h1 style={S.schoolName}>{school?.name}</h1>
          <p style={S.subtitle}>Enrollment Application · Complete all sections to submit</p>
        </div>

        {/* Progress */}
        <div style={S.progressBar}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={S.stepDot(i === step, i < step)} />
              <span style={{ fontSize: 10, fontWeight: 700, color: i <= step ? "#E8679A" : "#9CA3AF", textTransform: "uppercase" }}>{s}</span>
            </div>
          ))}
        </div>

        {error && <div style={S.error}>{error}</div>}

        {/* Step 0 — Child Info */}
        {step === 0 && (
          <div>
            <div style={S.sectionTitle}>Child Information</div>
            <div style={S.grid3}>
              <Field label="First Name" required><Input value={form.childFirst} onChange={f("childFirst")} placeholder="First name" /></Field>
              <Field label="Middle Name"><Input value={form.childMiddle} onChange={f("childMiddle")} placeholder="Middle name" /></Field>
              <Field label="Last Name" required><Input value={form.childLast} onChange={f("childLast")} placeholder="Last name" /></Field>
            </div>
            <div style={S.grid3}>
              <Field label="Date of Birth" required><Input type="date" value={form.dob} onChange={f("dob")} /></Field>
              <Field label="Gender"><Select value={form.gender} onChange={f("gender")} options={[{ value: "", label: "Select…" }, "Male", "Female", "Non-binary", "Prefer not to say"]} /></Field>
              <Field label="Age Group"><Select value={form.ageGroup} onChange={f("ageGroup")} options={[{ value: "", label: "Select…" }, ...AGE_GROUPS]} /></Field>
            </div>
            <Field label="Home Address"><Input value={form.address} onChange={f("address")} placeholder="Street, City, State, ZIP" /></Field>
            <div style={S.grid2}>
              <Field label="Desired Start Date"><Input type="date" value={form.desiredStart} onChange={f("desiredStart")} /></Field>
              <Field label="How did you hear about us?"><Input value={form.referralSource} onChange={f("referralSource")} placeholder="Word of mouth, social media…" /></Field>
            </div>
            <Field label="Previous School / Daycare"><Input value={form.previousSchool} onChange={f("previousSchool")} placeholder="If applicable" /></Field>
          </div>
        )}

        {/* Step 1 — Guardian Info */}
        {step === 1 && (
          <div>
            <div style={S.sectionTitle}>Primary Guardian</div>
            <div style={S.grid2}>
              <Field label="Full Name" required><Input value={form.parentName} onChange={f("parentName")} placeholder="Guardian full name" /></Field>
              <Field label="Relationship"><Select value={form.parentRelation} onChange={f("parentRelation")} options={["Mother", "Father", "Grandmother", "Grandfather", "Legal Guardian", "Other"]} /></Field>
            </div>
            <div style={S.grid2}>
              <Field label="Phone" required><Input type="tel" value={form.parentPhone} onChange={f("parentPhone")} placeholder="(555) 000-0000" /></Field>
              <Field label="Email" required><Input type="email" value={form.parentEmail} onChange={f("parentEmail")} placeholder="email@example.com" /></Field>
            </div>
            <div style={S.grid2}>
              <Field label="Employer"><Input value={form.parentEmployer} onChange={f("parentEmployer")} placeholder="Company name" /></Field>
              <Field label="Work Phone"><Input type="tel" value={form.parentWorkPhone} onChange={f("parentWorkPhone")} placeholder="(555) 000-0000" /></Field>
            </div>

            <div style={{ ...S.sectionTitle, marginTop: 24 }}>Secondary Guardian (optional)</div>
            <div style={S.grid2}>
              <Field label="Full Name"><Input value={form.parent2Name} onChange={f("parent2Name")} placeholder="Guardian full name" /></Field>
              <Field label="Relationship"><Select value={form.parent2Relation} onChange={f("parent2Relation")} options={["", "Mother", "Father", "Grandmother", "Grandfather", "Legal Guardian", "Other"]} /></Field>
            </div>
            <div style={S.grid2}>
              <Field label="Phone"><Input type="tel" value={form.parent2Phone} onChange={f("parent2Phone")} placeholder="(555) 000-0000" /></Field>
              <Field label="Email"><Input type="email" value={form.parent2Email} onChange={f("parent2Email")} placeholder="email@example.com" /></Field>
            </div>
          </div>
        )}

        {/* Step 2 — Medical */}
        {step === 2 && (
          <div>
            <div style={S.sectionTitle}>Medical Information</div>
            <Field label="Allergies"><Input value={form.allergies} onChange={f("allergies")} placeholder="List any known allergies or 'None'" /></Field>
            <Field label="Medications"><Input value={form.medications} onChange={f("medications")} placeholder="List any medications or 'None'" /></Field>
            <Field label="Special Needs / Accommodations"><Input value={form.specialNeeds} onChange={f("specialNeeds")} placeholder="IEP, 504, sensory needs, etc." /></Field>
            <div style={S.sectionTitle}>Physician</div>
            <div style={S.grid2}>
              <Field label="Physician Name"><Input value={form.physician} onChange={f("physician")} placeholder="Dr. Last Name" /></Field>
              <Field label="Physician Phone"><Input type="tel" value={form.physicianPhone} onChange={f("physicianPhone")} placeholder="(555) 000-0000" /></Field>
            </div>
            <div style={S.sectionTitle}>Insurance</div>
            <div style={S.grid2}>
              <Field label="Insurance Provider"><Input value={form.insuranceProvider} onChange={f("insuranceProvider")} placeholder="Provider name" /></Field>
              <Field label="Policy Number"><Input value={form.insurancePolicy} onChange={f("insurancePolicy")} placeholder="Policy #" /></Field>
            </div>
          </div>
        )}

        {/* Step 3 — Emergency */}
        {step === 3 && (
          <div>
            <div style={S.sectionTitle}>Emergency Contacts</div>
            {form.emergencyContacts.map((c, i) => (
              <div key={i} style={{ background: "#F9FAFB", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#6B7B90" }}>Contact {i + 1}</span>
                  {i > 0 && <button onClick={() => setForm(p => ({ ...p, emergencyContacts: p.emergencyContacts.filter((_, j) => j !== i) }))} style={S.removeBtn}>Remove</button>}
                </div>
                <div style={S.grid3}>
                  <Field label="Name"><Input value={c.name} onChange={e => setForm(p => { const a = [...p.emergencyContacts]; a[i] = { ...a[i], name: e.target.value }; return { ...p, emergencyContacts: a }; })} placeholder="Full name" /></Field>
                  <Field label="Relationship"><Input value={c.relation} onChange={e => setForm(p => { const a = [...p.emergencyContacts]; a[i] = { ...a[i], relation: e.target.value }; return { ...p, emergencyContacts: a }; })} placeholder="Aunt, Neighbor…" /></Field>
                  <Field label="Phone"><Input type="tel" value={c.phone} onChange={e => setForm(p => { const a = [...p.emergencyContacts]; a[i] = { ...a[i], phone: e.target.value }; return { ...p, emergencyContacts: a }; })} placeholder="(555) 000-0000" /></Field>
                </div>
              </div>
            ))}
            {form.emergencyContacts.length < 3 && <button style={S.addBtn} onClick={() => setForm(p => ({ ...p, emergencyContacts: [...p.emergencyContacts, { name: "", relation: "", phone: "" }] }))}>+ Add Emergency Contact</button>}

            <div style={{ ...S.sectionTitle, marginTop: 24 }}>Authorized Pickups</div>
            <p style={{ fontSize: 13, color: "#6B7B90", marginTop: -8, marginBottom: 14 }}>People authorized to pick up this child (other than guardians listed above)</p>
            {form.authorizedPickups.map((p, i) => (
              <div key={i} style={{ background: "#F9FAFB", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#6B7B90" }}>Person {i + 1}</span>
                  {i > 0 && <button onClick={() => setForm(prev => ({ ...prev, authorizedPickups: prev.authorizedPickups.filter((_, j) => j !== i) }))} style={S.removeBtn}>Remove</button>}
                </div>
                <div style={S.grid2}>
                  <Field label="Name"><Input value={p.name} onChange={e => setForm(prev => { const a = [...prev.authorizedPickups]; a[i] = { ...a[i], name: e.target.value }; return { ...prev, authorizedPickups: a }; })} placeholder="Full name" /></Field>
                  <Field label="Relationship"><Input value={p.relation} onChange={e => setForm(prev => { const a = [...prev.authorizedPickups]; a[i] = { ...a[i], relation: e.target.value }; return { ...prev, authorizedPickups: a }; })} placeholder="Grandparent, Neighbor…" /></Field>
                </div>
              </div>
            ))}
            {form.authorizedPickups.length < 4 && <button style={S.addBtn} onClick={() => setForm(p => ({ ...p, authorizedPickups: [...p.authorizedPickups, { name: "", relation: "" }] }))}>+ Add Authorized Pickup</button>}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
          {step > 0 && <button onClick={back} style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: "1.5px solid #E5E7EB", background: "#fff", color: "#6B7B90", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>}
          {step < STEPS.length - 1
            ? <button onClick={next} style={{ ...S.submitBtn, flex: 2, marginTop: 0 }}>Next: {STEPS[step + 1]} →</button>
            : <button onClick={submit} disabled={submitting} style={{ ...S.submitBtn, flex: 2, marginTop: 0, opacity: submitting ? 0.7 : 1 }}>{submitting ? "Submitting…" : "Submit Application ✓"}</button>
          }
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: "#9CA3AF", marginTop: 20 }}>
          This form is protected by reCAPTCHA. Your information is kept private and secure.
        </p>
      </div>
    </div>
  );
};

export default PublicEnrollmentForm;
